/**
 * GO Wild Area 2026: the whole week, 9 to 15 November, the curated schedule
 * behind the Profile's Wild Area card.
 *
 * Two events, one card. Into the Wild (Monday 9 to Friday 13) is the lead-in:
 * Crowned Zacian and Zamazenta in 5★ raids with a Raid Hour each, then a 5★
 * Max Battle Hour a day (Registeel, Regidrago, Latias and Latios), Druddigon
 * and Orthworm in 3★, Ralts and Machop in Max Battles, and Dynamax Meltan
 * through the GO Pass. GO Wild Area: Global (Saturday 14 and Sunday 15, 10 a.m.
 * to 6 p.m. local) is the main event: Steel and Dragon habitats taking turns on
 * the hour, Zekrom / Reshiram with the fused Kyurems, Dynamax Dialga and
 * Palkia, Gigantamax Duraludon, the Mighty roster, 10 km eggs.
 *
 * The live feed carries both events (ids below, start, end, a few linked
 * species) and nothing of their shape: no hours, no day-by-day line-up, no
 * roster. Those come from LeekDuck's event pages (cross-checked against the
 * official announcements), hand-copied here with sources and a verifiedAt,
 * the same auditable shape goFestHabitats.ts uses. The habitat clock reuses
 * that file's helpers outright: a Wild Area day is two habitats taking turns
 * on the hour, which is a GO Fest rotation with two entries.
 *
 * Every time here is wall-clock LOCAL, as the game runs the events: a window
 * is a local date plus hours and the helpers take the device's Date apart with
 * its local getters.
 *
 * Pure: no store, no DOM. The device prefs (ticket, snapshots) live at the
 * bottom with their own guarded localStorage reads.
 */
import {
  habitatHourLabel,
  habitatsAt,
  localDayKey,
  windowStartMs,
  type GoFestHabitat,
  type GoFestHabitatSchedule,
  type HabitatNow,
} from "@/lib/goFestHabitats";
import type { RaidDexVariant } from "@/lib/raidDexCrossRef";

export const WILD_AREA_2026_EVENT_ID = "pokemon-go-wild-area-2026-global";
export const INTO_THE_WILD_2026_EVENT_ID = "into-the-wild-2026";

export const WILD_AREA_SAT = "2026-11-14";
export const WILD_AREA_SUN = "2026-11-15";
/** The main event days (habitats, Mighty, eggs, the ticket), in order. */
export const WILD_AREA_DAYS: readonly string[] = [WILD_AREA_SAT, WILD_AREA_SUN];
/** Into the Wild, the lead-in week: a Raid Hour or Max Battle Hour a day. */
export const WILD_AREA_WEEK_DAYS: readonly string[] = [
  "2026-11-09", "2026-11-10", "2026-11-11", "2026-11-12", "2026-11-13",
];
/** Every day the card has a plan for, Monday to Sunday. */
export const WILD_AREA_ALL_DAYS: readonly string[] = [...WILD_AREA_WEEK_DAYS, ...WILD_AREA_DAYS];
/** Gates open and close on the main days, local hours. */
export const WILD_AREA_OPEN_HOUR = 10;
export const WILD_AREA_CLOSE_HOUR = 18;
/** The lead week's one hour that matters, 6 to 7 p.m. local. */
export const WILD_AREA_LEAD_HOUR = 18;
/**
 * The card appears the evening before the week starts (Monday is planned on
 * Sunday night) and leaves when Sunday's gates close.
 */
export const WILD_AREA_SHOW_FROM = { day: "2026-11-08", hour: 18 } as const;

export interface WildAreaSpecies {
  speciesId: number;
  /** A regional or costume form: the tile and its dex row are the form's. */
  formId?: string;
  /** The one habitat spawn the page lists without a shiny: no shiny tile. */
  noShiny?: boolean;
}

export interface WildAreaHabitat {
  key: string;
  name: string;
  /** The one-word name the section lines use ("Steel"), the full one is the headline. */
  short: string;
  /** The type's colour, from the app's own type table (Steel / Dragon). */
  color: string;
  /** Local start hours of its one-hour windows, the same on both days. */
  hours: readonly number[];
  species: readonly WildAreaSpecies[];
}

export type WildAreaRaidTier = "1max" | "2max" | "3" | "5" | "5max" | "dynamax" | "gmax";

export interface WildAreaRaidBoss extends WildAreaSpecies {
  tier: WildAreaRaidTier;
  /** The dex the tile tracks and writes: Main for raids, D-Max / G-Max for Max Battles. */
  variant: RaidDexVariant;
  /** The caption under the tile ("3★ · Jacket", "5★ · Fusion"). */
  caption: string;
}

/** A lead day's hour: a 5★ raid boss in every Gym, or a 5★ Max Battle at every Power Spot. */
export interface WildAreaHour {
  kind: "raid-hour" | "max-hour";
  startHour: number;
  endHour: number;
  /** "Raid Hour" / "Max Battle Hour", the kind spelled for the card. */
  label: string;
  bosses: readonly WildAreaRaidBoss[];
}

export interface WildAreaDay {
  day: string;
  /** "lead" is Into the Wild (Monday to Friday), "main" the Wild Area weekend. */
  phase: "lead" | "main";
  raids: readonly WildAreaRaidBoss[];
  /** The hour that matters on a lead day. Absent on the weekend, whose clock is the habitats'. */
  hour?: WildAreaHour;
  /** Mighty Pokémon in the wild that day: higher stats, likely XL or XXL. Weekend only. */
  mighty?: readonly WildAreaSpecies[];
}

export interface WildAreaAllowance {
  raidPasses: number;
  maxParticles: number;
  snapshots: number;
}

export interface WildAreaSchedule {
  eventId: string;
  habitats: readonly WildAreaHabitat[];
  days: readonly WildAreaDay[];
  eggs10km: readonly WildAreaSpecies[];
  free: WildAreaAllowance;
  ticket: WildAreaAllowance;
  /** Bonus lines for everyone, 10 a.m. to 6 p.m. on the main days. */
  bonusesFree: readonly string[];
  /** Bonus lines a ticket adds. */
  bonusesTicket: readonly string[];
  /** Into the Wild: what is out all week, and the GO Pass's Dynamax debut. */
  lead: {
    eventId: string;
    spawns: readonly WildAreaSpecies[];
    goPass: readonly WildAreaRaidBoss[];
    bonuses: readonly string[];
  };
  sources: readonly string[];
  verifiedAt: string;
}

const STEEL_HOURS = [10, 12, 14, 16] as const;
const DRAGON_HOURS = [11, 13, 15, 17] as const;

const sp = (speciesId: number, formId?: string): WildAreaSpecies =>
  formId ? { speciesId, formId } : { speciesId };

// Into the Wild's line-up. The crowned 5★ pair and the 3★ pair run all week;
// each day adds its own hour's boss. Ralts and Machop are the week's 1★ and 2★
// Max Battles.
// "5★" alone: the tile already says CROWNED under the name.
const ZACIAN: WildAreaRaidBoss = { speciesId: 888, formId: "zacian-crowned", tier: "5", variant: "pokemon", caption: "5★" };
const ZAMAZENTA: WildAreaRaidBoss = { speciesId: 889, formId: "zamazenta-crowned", tier: "5", variant: "pokemon", caption: "5★" };
const DRUDDIGON: WildAreaRaidBoss = { speciesId: 621, tier: "3", variant: "pokemon", caption: "3★" };
const ORTHWORM: WildAreaRaidBoss = { speciesId: 968, tier: "3", variant: "pokemon", caption: "3★" };
const MAX_RALTS: WildAreaRaidBoss = { speciesId: 280, tier: "1max", variant: "dynamax", caption: "1★ Max" };
const MAX_MACHOP: WildAreaRaidBoss = { speciesId: 66, tier: "2max", variant: "dynamax", caption: "2★ Max" };
const MAX_REGISTEEL: WildAreaRaidBoss = { speciesId: 379, tier: "5max", variant: "dynamax", caption: "5★ Max" };
const MAX_REGIDRAGO: WildAreaRaidBoss = { speciesId: 895, tier: "5max", variant: "dynamax", caption: "5★ Max · debut" };
const MAX_LATIAS: WildAreaRaidBoss = { speciesId: 380, tier: "5max", variant: "dynamax", caption: "5★ Max" };
const MAX_LATIOS: WildAreaRaidBoss = { speciesId: 381, tier: "5max", variant: "dynamax", caption: "5★ Max" };

const raidHour = (boss: WildAreaRaidBoss): WildAreaHour => ({
  kind: "raid-hour",
  startHour: WILD_AREA_LEAD_HOUR,
  endHour: WILD_AREA_LEAD_HOUR + 1,
  label: "Raid Hour",
  bosses: [boss],
});
const maxHour = (...bosses: WildAreaRaidBoss[]): WildAreaHour => ({
  kind: "max-hour",
  startHour: WILD_AREA_LEAD_HOUR,
  endHour: WILD_AREA_LEAD_HOUR + 1,
  label: "Max Battle Hour",
  bosses,
});
const leadDay = (day: string, hour: WildAreaHour): WildAreaDay => ({
  day,
  phase: "lead",
  hour,
  // The hour's bosses lead the line-up, then the week's constants.
  raids: [
    ...hour.bosses,
    ...[ZACIAN, ZAMAZENTA].filter((b) => !hour.bosses.includes(b)),
    DRUDDIGON,
    ORTHWORM,
    MAX_RALTS,
    MAX_MACHOP,
  ],
});

export const WILD_AREA_2026: WildAreaSchedule = {
  eventId: WILD_AREA_2026_EVENT_ID,
  // LeekDuck's pages are the ones cited; the official event pages say the same
  // and are deliberately not linked here (the IP gate keeps that domain out of
  // the shipping tree, see scripts/ip-check.sh).
  sources: [
    "https://leekduck.com/events/pokemon-go-wild-area-2026-global/",
    "https://leekduck.com/events/into-the-wild-2026/",
  ],
  verifiedAt: "2026-10-20",
  habitats: [
    {
      key: "solid-as-steel",
      name: "Solid as Steel",
      short: "Steel",
      color: "#B8B8D0",
      hours: STEEL_HOURS,
      species: [
        sp(27, "sandshrew-alola"),
        sp(50, "diglett-alola"),
        sp(81), sp(90), sp(95), sp(137), sp(213), sp(227), sp(299), sp(304),
        sp(374), sp(393), sp(410), sp(436), sp(448), sp(529), sp(597), sp(599),
        { speciesId: 618, formId: "stunfisk-galar", noShiny: true },
        sp(632), sp(821), sp(957),
      ],
    },
    {
      key: "daring-as-dragons",
      name: "Daring as Dragons",
      short: "Dragons",
      color: "#7038F8",
      hours: DRAGON_HOURS,
      species: [
        sp(4), sp(129), sp(147), sp(207), sp(328), sp(333), sp(339), sp(371),
        sp(443), sp(602), sp(610), sp(631), sp(633), sp(690), sp(696), sp(698),
        sp(704), sp(714), sp(776), sp(782), sp(850), sp(996),
      ],
    },
  ],
  days: [
    leadDay("2026-11-09", raidHour(ZACIAN)),
    leadDay("2026-11-10", raidHour(ZAMAZENTA)),
    leadDay("2026-11-11", maxHour(MAX_REGISTEEL)),
    leadDay("2026-11-12", maxHour(MAX_REGIDRAGO)),
    leadDay("2026-11-13", maxHour(MAX_LATIAS, MAX_LATIOS)),
    {
      day: WILD_AREA_SAT,
      phase: "main",
      raids: [
        { speciesId: 68, formId: "machamp-wildarea-2026", tier: "3", variant: "pokemon", caption: "3★ · Jacket" },
        { speciesId: 644, tier: "5", variant: "pokemon", caption: "5★" },
        { speciesId: 646, formId: "kyurem-white", tier: "5", variant: "pokemon", caption: "5★ · Fusion" },
        { speciesId: 483, tier: "dynamax", variant: "dynamax", caption: "Dynamax" },
        { speciesId: 884, tier: "gmax", variant: "gmax", caption: "6★ · G-Max" },
      ],
      mighty: [sp(103, "exeggutor-alola"), sp(212), sp(330), sp(365), sp(376), sp(395), sp(462), sp(534), sp(612)],
    },
    {
      day: WILD_AREA_SUN,
      phase: "main",
      raids: [
        { speciesId: 68, formId: "machamp-wildarea-2026", tier: "3", variant: "pokemon", caption: "3★ · Jacket" },
        { speciesId: 643, tier: "5", variant: "pokemon", caption: "5★" },
        { speciesId: 646, formId: "kyurem-black", tier: "5", variant: "pokemon", caption: "5★ · Fusion" },
        { speciesId: 484, tier: "dynamax", variant: "dynamax", caption: "Dynamax" },
        { speciesId: 884, tier: "gmax", variant: "gmax", caption: "6★ · G-Max" },
      ],
      mighty: [sp(184), sp(230), sp(306), sp(598), sp(635), sp(706), sp(715), sp(959), sp(980)],
    },
  ],
  eggs10km: [sp(679), sp(707), sp(780), sp(884)],
  free: { raidPasses: 3, maxParticles: 1600, snapshots: 3 },
  ticket: { raidPasses: 6, maxParticles: 3200, snapshots: 6 },
  // Terse on purpose: the pills share a 300px row, so each line is a label,
  // not the page's sentence (the sources carry the full wording).
  bonusesFree: [
    "GO Safari Balls",
    "Party Play 8 h",
    "Mighty on Routes",
    "More event research",
  ],
  bonusesTicket: [
    "Shiny boost",
    "2× catch candy",
    "2× Incense time",
    "Lures 2 h",
    "More 10 km eggs",
    "6 Special Trades",
    "½ trade dust",
    "Hourly Supply Balloons",
  ],
  lead: {
    eventId: INTO_THE_WILD_2026_EVENT_ID,
    spawns: [sp(81), sp(714), sp(777)],
    goPass: [{ speciesId: 808, tier: "dynamax", variant: "dynamax", caption: "GO Pass · debut" }],
    bonuses: [
      "Remote Raids 30 a day",
      "Shiny Gimmighoul from Coin Bags",
      "Shiny Meltan from the Mystery Box",
      "Daily Discoveries paused all week",
    ],
  },
};

/**
 * The habitat rotation as a GoFestHabitatSchedule, so the fest helpers
 * (habitatsAt, windows) answer "which habitat is on" for the Wild Area too.
 * Species ids are the base ids; the card reads forms from WILD_AREA_2026
 * itself, this is the clock only.
 */
export function wildAreaHabitatSchedule(schedule: WildAreaSchedule = WILD_AREA_2026): GoFestHabitatSchedule {
  const habitats: GoFestHabitat[] = schedule.habitats.map((h) => ({
    key: h.key,
    name: h.name,
    color: h.color,
    speciesIds: h.species.map((s) => s.speciesId),
    windows: WILD_AREA_DAYS.flatMap((day) =>
      h.hours.map((startHour) => ({ day, startHour, endHour: startHour + 1 })),
    ),
  }));
  return {
    eventId: schedule.eventId,
    liveFromHour: 0,
    habitats,
    sources: schedule.sources,
    verifiedAt: schedule.verifiedAt,
  };
}

/** Local epoch ms for a local day at an hour. */
export function wildAreaLocalMs(day: string, hour: number, minute = 0): number {
  return windowStartMs({ day, startHour: hour, endHour: hour }) + minute * 60_000;
}

export type WildAreaHourState = "before" | "live" | "after";

export interface WildAreaNow {
  /** False outside the show window: the card renders nothing. */
  visible: boolean;
  /**
   * The day the card opens on: today while it is an event day that is not
   * over (a lead day lasts to midnight, a main day closes at 6 p.m.), else
   * the next event day. Null once the week is over.
   */
  focusDay: string | null;
  /** The focus day's phase, so the card knows which shape to draw. */
  phase: "lead" | "main" | null;
  /** Main days only: gates open right now on the device's clock. */
  live: boolean;
  /** Main days only: when the focus day's gates open, for the countdown; null while live. */
  opensAt: number | null;
  /** Lead days only: where the day's hour stands, with its bounds. */
  hour: { state: WildAreaHourState; startsAt: number; endsAt: number } | null;
  /** The habitat rotation at `now` (current / next), from the fest helpers. */
  rotation: HabitatNow;
}

/** Where the week stands at `now`, by the device's local clock. */
export function wildAreaAt(now: number = Date.now(), schedule: WildAreaSchedule = WILD_AREA_2026): WildAreaNow {
  const showFrom = wildAreaLocalMs(WILD_AREA_SHOW_FROM.day, WILD_AREA_SHOW_FROM.hour);
  const lastDay = WILD_AREA_DAYS[WILD_AREA_DAYS.length - 1];
  const showUntil = wildAreaLocalMs(lastDay, WILD_AREA_CLOSE_HOUR);
  const rotation = habitatsAt(wildAreaHabitatSchedule(schedule), now);
  const hidden: WildAreaNow = { visible: false, focusDay: null, phase: null, live: false, opensAt: null, hour: null, rotation };
  if (now < showFrom || now >= showUntil) return hidden;

  const today = localDayKey(now);
  const todayPlan = wildAreaDay(today, schedule);
  const closedForToday =
    todayPlan?.phase === "main" && now >= wildAreaLocalMs(today, WILD_AREA_CLOSE_HOUR);
  const focusDay =
    todayPlan && !closedForToday ? today : WILD_AREA_ALL_DAYS.find((d) => d > today) ?? null;
  const plan = focusDay ? wildAreaDay(focusDay, schedule) : null;
  if (!focusDay || !plan) return hidden;

  if (plan.phase === "lead" && plan.hour) {
    const startsAt = wildAreaLocalMs(focusDay, plan.hour.startHour);
    const endsAt = wildAreaLocalMs(focusDay, plan.hour.endHour);
    const state: WildAreaHourState = now < startsAt ? "before" : now < endsAt ? "live" : "after";
    return { visible: true, focusDay, phase: "lead", live: false, opensAt: null, hour: { state, startsAt, endsAt }, rotation };
  }
  const opensAt = wildAreaLocalMs(focusDay, WILD_AREA_OPEN_HOUR);
  const live = focusDay === today && now >= opensAt;
  return { visible: true, focusDay, phase: "main", live, opensAt: live ? null : opensAt, hour: null, rotation };
}

/** The day after `day` on the week's plan, or null on Sunday. */
export function wildAreaNextDay(day: string): string | null {
  const i = WILD_AREA_ALL_DAYS.indexOf(day);
  return i >= 0 && i + 1 < WILD_AREA_ALL_DAYS.length ? WILD_AREA_ALL_DAYS[i + 1] : null;
}

/** The day before `day` on the week's plan, or null on Monday. */
export function wildAreaPrevDay(day: string): string | null {
  const i = WILD_AREA_ALL_DAYS.indexOf(day);
  return i > 0 ? WILD_AREA_ALL_DAYS[i - 1] : null;
}

/** The other main day, for the weekend's own flip (Saturday to Sunday and back). */
export function wildAreaOtherDay(day: string): string | null {
  return WILD_AREA_DAYS.find((d) => d !== day) ?? null;
}

/** The curated day entry for a local day key, or null off the schedule. */
export function wildAreaDay(day: string, schedule: WildAreaSchedule = WILD_AREA_2026): WildAreaDay | null {
  return schedule.days.find((d) => d.day === day) ?? null;
}

/** The habitat that opens a day (both days start on Steel). */
export function wildAreaFirstHabitat(schedule: WildAreaSchedule = WILD_AREA_2026): WildAreaHabitat {
  return [...schedule.habitats].sort((a, b) => Math.min(...a.hours) - Math.min(...b.hours))[0];
}

/** A habitat by key, or null. */
export function wildAreaHabitat(key: string, schedule: WildAreaSchedule = WILD_AREA_2026): WildAreaHabitat | null {
  return schedule.habitats.find((h) => h.key === key) ?? null;
}

/** "Sat 14 Nov", in the device's locale. */
export function wildAreaDayLabel(day: string): string {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
}

/** "Mon" / "Sat", for the week strip. */
export function wildAreaWeekdayLabel(day: string): string {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: "short" });
}

/** The day of the month, for the week strip's second line. */
export function wildAreaDayNumber(day: string): number {
  return Number(day.split("-")[2]);
}

/** "10 am", the fest block's own hour spelling. */
export const wildAreaHourLabel = habitatHourLabel;

/**
 * The eight one-hour segments of a main day, in order, each with its
 * habitat and whether it is the live one at `now`: the card's hour strip.
 */
export function wildAreaHourStrip(
  day: string,
  now: number,
  schedule: WildAreaSchedule = WILD_AREA_2026,
): Array<{ hour: number; habitat: WildAreaHabitat; live: boolean; over: boolean }> {
  const out: Array<{ hour: number; habitat: WildAreaHabitat; live: boolean; over: boolean }> = [];
  for (let hour = WILD_AREA_OPEN_HOUR; hour < WILD_AREA_CLOSE_HOUR; hour += 1) {
    const habitat = schedule.habitats.find((h) => h.hours.includes(hour));
    if (!habitat) continue;
    const start = wildAreaLocalMs(day, hour);
    const end = wildAreaLocalMs(day, hour + 1);
    out.push({ hour, habitat, live: start <= now && now < end, over: now >= end });
  }
  return out;
}

export interface WildAreaGap {
  variant: RaidDexVariant;
  speciesId: number;
  formId?: string;
}

/**
 * The gap tiles for a species list: one per (species, variant) the trainer
 * is missing, species order then Main before Shiny. The schedule is the
 * authority on what can be caught here (every habitat spawn is listed shiny
 * except the one flagged), so no roster gate: a release roster that lags the
 * event must not hide a tile for something spawning outside right now.
 */
export function wildAreaGaps(
  species: readonly WildAreaSpecies[],
  variants: readonly RaidDexVariant[],
  isPresent: (variant: RaidDexVariant, speciesId: number, formId?: string) => boolean,
): WildAreaGap[] {
  const out: WildAreaGap[] = [];
  for (const s of species) {
    for (const variant of variants) {
      if (variant === "shiny" && s.noShiny) continue;
      if (isPresent(variant, s.speciesId, s.formId)) continue;
      out.push(s.formId ? { variant, speciesId: s.speciesId, formId: s.formId } : { variant, speciesId: s.speciesId });
    }
  }
  return out;
}

/** The species of a list the trainer already has in `variant`. */
export function wildAreaOwned(
  species: readonly WildAreaSpecies[],
  variant: RaidDexVariant,
  isPresent: (variant: RaidDexVariant, speciesId: number, formId?: string) => boolean,
): WildAreaSpecies[] {
  return species.filter((s) => isPresent(variant, s.speciesId, s.formId));
}

// ─── Device prefs ────────────────────────────────────────────────────────────
// The ticket switch and the snapshot ticks are per device: a ticket is bought
// per account, and nobody wants to re-tick three photos on another phone, but
// neither is worth a server round trip for a two-day event.

export const WILD_AREA_PREFS_EVENT = "nexus:wild-area-prefs-changed";
const TICKET_KEY = "nexus.wildArea2026.ticket.v1";
const SNAPS_KEY = "nexus.wildArea2026.snaps.v1";

function storage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

function announce(): void {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(WILD_AREA_PREFS_EVENT));
}

export function readWildAreaTicket(): boolean {
  return storage()?.getItem(TICKET_KEY) === "1";
}

export function writeWildAreaTicket(on: boolean): void {
  try {
    if (on) storage()?.setItem(TICKET_KEY, "1");
    else storage()?.removeItem(TICKET_KEY);
  } catch {
    /* private mode: the switch still flips for this render */
  }
  announce();
}

function readSnapMap(): Record<string, number> {
  try {
    const raw = storage()?.getItem(SNAPS_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    return parsed && typeof parsed === "object" ? (parsed as Record<string, number>) : {};
  } catch {
    return {};
  }
}

/** Snapshot encounters ticked off on a local day, 0..allowance. */
export function readWildAreaSnapshots(day: string): number {
  const n = readSnapMap()[day];
  return typeof n === "number" && Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

export function writeWildAreaSnapshots(day: string, count: number): void {
  try {
    const map = readSnapMap();
    map[day] = Math.max(0, Math.floor(count));
    storage()?.setItem(SNAPS_KEY, JSON.stringify(map));
  } catch {
    /* same */
  }
  announce();
}

export function __resetWildAreaPrefsForTest(): void {
  try {
    storage()?.removeItem(TICKET_KEY);
    storage()?.removeItem(SNAPS_KEY);
  } catch {
    /* nothing to reset */
  }
}
